
export function wrapIndex<T>(index: number, array: T[]): T {
    return array[normalizeIndex(index, array)];
}

export function normalizeIndex<T>(index: number, array: T[]): number {
    if (!array || !array.length) {
        return null;
    }

    while (index < 0) {
        index += array.length;
    }
    while (index >= array.length) {
        index -= array.length;
    }

    return index;
}

export function contains<T>(arr: T[], value: T): boolean {
    for (const v of arr) {
        if (v === value) {
            return true;
        }
    }
    return false;
}

/**
 * Search an array for the closest value to a target
 */
export function nearest(target: number, arr: number[]) {
    let nearestValue: number = -1;
    let dist = Number.MAX_VALUE;

    for (const n of arr) {
        if (Math.abs(n - target) < dist) {
            dist = Math.abs(n - target);
            nearestValue = n;
        }
    }

    return nearestValue;
}

/**
 * Search an array for the closest value to a target, without ever going over it
 */
export function nearestUnder(target: number, arr: number[]) {
    let nearestValue: number = -1;
    let dist = Number.MAX_VALUE;

    for (const n of arr) {
        if (n > target) {
            continue;
        }
        if (Math.abs(n - target) < dist) {
            dist = Math.abs(n - target);
            nearestValue = n;
        }
    }

    return nearestValue;
}

export function unique<T>(arr: T[]) {
    const uniqueArr = [];
    for (const v of arr) {
        if (!contains(uniqueArr, v)) {
            uniqueArr.push(v);
        }
    }
    return uniqueArr;
}

/**
 * Removes any element which rejectArr contains from sourceArr
 * @param sourceArr {T[]}
 * @param rejectArr {T[]}
 */
export function reject<T>(sourceArr: T[], rejectArr: T[]) {
    const rejectedArr = [];
    for (const v of sourceArr) {
        if (!contains(rejectArr, v)) {
            rejectedArr.push(v);
        }
    }
    return rejectedArr;
}

/**
 * Pushes the same value to the array 'count' number of times
 * @param arr {T[]}
 * @param value {T}
 * @param count {number}
 */
export function addRepeated<T>(arr: T[], value: T, count: number) {
    while (count > 0) {
        count--;
        arr.push(value);
    }
}

export function clearMap<T>(map: Map<string, T>) {
    if (map) {
        map.forEach((obj: any) => {
            if (obj.destroy) {
                obj.destroy();
            }
        });

        map.clear();
    }
}

export function lowerCaseMapKeys(map: Map<string, any>) {
    map.forEach((value: any, key: string) => {
        map.set(key.toLowerCase(), map.get(key));
    });
}

export function lowerCaseMapValues(map: Map<any, string>) {
    map.forEach((value: string, key: any) => {
        map.set(key, map.get(key).toLowerCase());
    });
}

export function shuffle<T>(a: T[]) {
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

export function asArray<T>(target: T | T[]): T[] {
    if (Array.isArray(target)) {
        return target;
    } else {
        return [target];
    }
}

export function lastInArray<T>(target: T | T[]): T {
    if (Array.isArray(target)) {
        return target[target.length - 1];
    } else {
        return target;
    }
}

export function firstInArray<T>(target: T | T[]): T {
    if (Array.isArray(target)) {
        return target[0];
    } else {
        return target;
    }
}

/**
 * Create an array of given length with an optional default value
 *
 * @param length
 * @param defaultValue
 * @returns An array of indices, or an array of the default value
 */
export function arrayOfValues<T>(length: number, defaultValue?: T): T[] {
    const arr: T[] = Array.apply(null, {length});
    if (defaultValue !== undefined) {
        return arr.map(() => defaultValue);
    }
    return arr.map(Number.call, Number);
}

/**
 * Count the occurances of a certain value inside an array
 */
export function countOccurances<T>(target: T, arr: T[]): number {
    return arr.reduce((count: number, item: T) => count += Number(item === target), 0);
}
